// Schedule API — типизированные обёртки над backend-эндпоинтами.
// Контракт сверен с apps/api schedule.controller + schedule.dto + list-occurrences.query.

import type { NotHeldReason } from '@app/domain';

import { ApiError, api } from '@/lib/api';
import { branchQueryString, setBranchParams } from '@/lib/branch-params';

/** База API — та же, что у `api`-хелпера: скачивание файла идёт мимо него. */
const BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

export type GroupType = 'regular' | 'personal' | 'wedding' | 'masterclass';
export type GroupStatus = 'active' | 'paused' | 'archived';
export type OccurrenceStatus = 'scheduled' | 'completed' | 'cancelled' | 'moved' | 'not_held';

/** Тип последней правки занятия — для ярлыка «сменился педагог/зал/ёмкость». */
export type OccurrenceChangeKind = 'teacher_changed' | 'hall_changed' | 'capacity_changed';

export interface GroupListItem {
  id: string;
  name: string;
  type: string;
  duration_minutes: number;
  capacity: number | null;
  description: string | null;
  /** Пометка в афише группы («С ОКТЯБРЯ»), план 145. */
  poster_note: string | null;
  video_url: string | null;
  status: GroupStatus;
  /** Возрастная группа из справочника — фильтр «Возраст» в «Поделиться» (план 145). */
  age_group_id: string | null;
  branch_ids: string[];
  teacher_ids: string[];
  /**
   * Правка владельца 04.09: педагог/расписание/филиал по ЗАНЯТИЯМ, когда
   * `teacher_ids` пуст (65 из 85 активных групп на проде без явной привязки).
   * См. `apps/admin/src/features/reference/api/catalog.api.ts` (ScheduleGroup)
   * — там же используется столбцом «Педагог»/«Расписание» справочника.
   */
  effective_teacher_ids: string[];
  effective_teacher_names: string[];
  schedule_note: string | null;
  branch_names: string[];
}

export interface CreateGroupBody {
  name: string;
  type?: GroupType;
  duration_minutes?: number;
  capacity?: number | null;
  description?: string;
  branch_ids?: string[];
  teacher_ids?: string[];
}

export interface CreateGroupResult {
  id: string;
}

/** Тип занятия — совпадает с {@link GroupType}, но отдаётся per-occurrence. */
export type ClassType =
  | 'regular'
  | 'personal'
  | 'wedding'
  | 'masterclass'
  | 'rental'
  | 'self_training';

/**
 * Вид занятия, выбираемый при создании (backend occurrence_kind):
 *   group — групповое; individual — индивидуальное; rental — аренда (без педагога);
 *   self_training — самостоятельная тренировка педагога (без клиентов).
 */
export type OccurrenceKind = 'group' | 'individual' | 'rental' | 'self_training';

// Транспорт occurrence: scheduledAt приходит как ISO-строка (camelCase из query).
export interface OccurrenceItem {
  id: string;
  /** Короткий номер занятия (план 116) — ссылка /schedule/1234. */
  no: string | null;
  seriesId: string | null;
  groupId: string;
  branchId: string;
  hallId: string;
  teacherId: string;
  scheduledAt: string;
  durationMinutes: number;
  maxAttendees: number;
  status: OccurrenceStatus;
  movedToOccurrenceId: string | null;
  cancellationReason: string | null;
  /**
   * Причина «Не состоялось» (план 153). NULL — не `not_held` либо помечено до
   * плана («причина не указана»). Опционально: старый кэш поля не знает.
   */
  notHeldReason?: NotHeldReason | null;
  /** Тип последней правки (сменился педагог/зал/ёмкость) — для ярлыка. NULL — не правилось. */
  lastChangeKind: OccurrenceChangeKind | null;
  /** Вид занятия (group/individual/rental/self_training) — фильтр абонементов при записи (D4). */
  occurrenceKind: OccurrenceKind;
  // Денормализованные поля для рендера сетки без доп. запросов (A1.1).
  groupName: string;
  classType: ClassType;
  teacherName: string;
  hallName: string;
  directionId: string | null;
  directionName: string | null;
  capacity: number | null;
  /** Записано клиентов (активные записи, без листа ожидания). */
  enrolledCount: number;
  /** Записей без отметки визита — для подсветки прошедших неотмеченных занятий. */
  pendingMarkCount: number;
  /**
   * Ведёт не штатный педагог серии (батч №23 П11) — плитка помечает замену.
   * Опционально: старый кэш и фикстуры тестов поля не знают.
   */
  hasSubstitute?: boolean;
  /** Первые ~6 записанных клиентов — для кружков-инициалов в ярлыке занятия. */
  enrolledClients: Array<{ id: string; firstName: string; lastName: string }>;
}

export interface ListOccurrencesResult {
  items: OccurrenceItem[];
  nextCursor: string | null;
  hasMore: boolean;
  limit: number;
}

export interface ListOccurrencesParams {
  /** ISO-8601 c offset, e.g. 2026-05-25T00:00:00.000Z. */
  from: string;
  to: string;
  /** Филиалы шапки (план 106, мультивыбор); пусто — все. */
  branchIds?: string[];
  groupId?: string;
  teacherId?: string;
  hallId?: string;
  directionId?: string;
  /** Категория групп — уровень выше направления (план 104 Б2). */
  categoryId?: string;
  classType?: ClassType;
  status?: OccurrenceStatus;
  limit?: number;
  cursor?: string;
}

export interface CreateSeriesBody {
  /**
   * Обязательна только для групповых серий. Аренда, индивидуальное и
   * самотренировка идут без неё — бэкенд подберёт системную (план 103).
   */
  group_id?: string;
  branch_id: string;
  hall_id: string;
  /** Обязателен для всех типов, КРОМЕ аренды (rental): для аренды — null/пропуск. */
  teacher_id?: string | null;
  /** Вид занятия; по умолчанию (пропуск) — 'group'. */
  occurrence_kind?: OccurrenceKind;
  /** RRULE, e.g. FREQ=WEEKLY;BYDAY=MO,WE;BYHOUR=18;BYMINUTE=0. */
  recurrence_rule: string;
  /** ISO-8601 c offset. */
  starts_at: string;
  ends_at?: string | null;
  default_max_attendees?: number;
  pre_generate?: boolean;
  /** Длительность занятий серии, мин (план 103). Пусто — длительность группы. */
  duration_minutes?: number;
  /** Клиент серии (аренда/индивидуальное): записывается на каждое занятие. */
  client_id?: string;
}

export interface CreateSeriesResult {
  id: string;
  occurrences_generated: number;
}

export interface HallItem {
  id: string;
  branch_id: string;
  name: string;
}

export interface TeacherItem {
  id: string;
  first_name: string;
  last_name: string;
}

export interface ListScheduleGroupsParams {
  /** Филиал из шапки: группы чужого филиала в выпадашке только мешают. */
  /** Филиалы шапки (план 106, мультивыбор); пусто — все. */
  branchIds?: string[];
  includeArchived?: boolean;
}

export async function listGroups(
  params: ListScheduleGroupsParams = {},
): Promise<{ items: GroupListItem[] }> {
  const sp = new URLSearchParams();
  // Архив и филиал отсекает СЕРВЕР. Раньше запрос уходил вовсе без параметров:
  // сервер отдавал первые 100 строк из 342, и архивные вычёркивались уже здесь
  // — в выпадашке молча не хватало живых групп (правка владельца 13.08).
  if (!params.includeArchived) sp.set('status', 'active');
  setBranchParams(sp, params.branchIds);
  sp.set('limit', '1000');
  const res = await api.get<{ items: GroupListItem[] }>(`/schedule/groups?${sp.toString()}`);
  return { items: res.items };
}

export function listHalls(branchIds?: string[]): Promise<{ items: HallItem[] }> {
  return api.get<{ items: HallItem[] }>(`/schedule/halls${branchQueryString(branchIds)}`);
}

export function listTeachers(branchIds?: string[]): Promise<{ items: TeacherItem[] }> {
  return api.get<{ items: TeacherItem[] }>(`/schedule/teachers${branchQueryString(branchIds)}`);
}

export function createGroup(body: CreateGroupBody): Promise<CreateGroupResult> {
  return api.post<CreateGroupResult>('/schedule/groups', body);
}

function buildOccurrencesQuery(params: ListOccurrencesParams): string {
  const sp = new URLSearchParams();
  sp.set('from', params.from);
  sp.set('to', params.to);
  setBranchParams(sp, params.branchIds);
  if (params.groupId) sp.set('group_id', params.groupId);
  if (params.teacherId) sp.set('teacher_id', params.teacherId);
  if (params.hallId) sp.set('hall_id', params.hallId);
  if (params.directionId) sp.set('direction_id', params.directionId);
  if (params.categoryId) sp.set('category_id', params.categoryId);
  if (params.classType) sp.set('class_type', params.classType);
  if (params.status) sp.set('status', params.status);
  if (params.limit !== undefined) sp.set('limit', String(params.limit));
  if (params.cursor) sp.set('cursor', params.cursor);
  return `?${sp.toString()}`;
}

export function listOccurrences(params: ListOccurrencesParams): Promise<ListOccurrencesResult> {
  return api.get<ListOccurrencesResult>(`/schedule/occurrences${buildOccurrencesQuery(params)}`);
}

/**
 * GET /schedule/occurrences/:id — одно занятие (тот же обогащённый формат, что в списке).
 * Для попапа-карточки занятия из других разделов (клик по посещению в карточке
 * абонемента → OccurrenceDrawer).
 */
export function getOccurrence(id: string): Promise<OccurrenceItem> {
  return api.get<OccurrenceItem>(`/schedule/occurrences/${id}`);
}

/**
 * Группы для выбора ведомости (план 121, правка владельца 01.09).
 *
 * Отдельно от справочника групп: названия НЕ уникальны, и по одному имени
 * администратор выбрать не может — нужны педагог, зал, число занятий месяца и
 * отметка о печати.
 */
export interface RosterGroupItem {
  group_id: string;
  name: string;
  teacher_name: string | null;
  hall_name: string | null;
  branch_name: string | null;
  /** «ср, пт 17:00» — чем различаются тёзки одного педагога. */
  schedule_note: string | null;
  occurrences_count: number;
  printed_at: string | null;
  printed_by_name: string | null;
}

export function listRosterGroups(params: {
  month: string;
  branchIds?: string[];
}): Promise<RosterGroupItem[]> {
  const q = new URLSearchParams({ month: params.month });
  if (params.branchIds?.length) q.set('branch_ids', params.branchIds.join(','));
  return api.get<{ data: RosterGroupItem[] }>(`/schedule/roster-groups?${q}`).then((r) => r.data);
}

/** Отметить ведомость распечатанной — отметку видят все администраторы. */
export function markRosterPrinted(
  groupId: string,
  month: string,
): Promise<{ group_id: string; month: string; printed_at: string }> {
  return api.post(`/schedule/groups/${encodeURIComponent(groupId)}/roster-printed`, { month });
}

/**
 * Вид печатной ведомости (план 121, правка владельца 01.09): состав, порядок,
 * названия и ширины столбцов + логотип школы. Одна раскладка на школу.
 */
export interface RosterColumn {
  /** 'num' | 'name' | 'age' | 'representative' | 'phone' | 'dates' | 'extra:<slug>'. */
  key: string;
  label: string;
  /** Ширина в миллиметрах; 0 — по содержимому (используется у колонок дат). */
  width: number;
  visible: boolean;
}

export interface RosterLayout {
  columns: RosterColumn[];
  logo_data: string | null;
  customized: boolean;
}

export function getRosterLayout(): Promise<RosterLayout> {
  return api.get<RosterLayout>('/schedule/roster-layout');
}

export function saveRosterLayout(body: {
  columns: RosterColumn[];
  logo_data: string | null;
}): Promise<RosterLayout> {
  return api.put<RosterLayout>('/schedule/roster-layout', body);
}

/** Ведомость группы на месяц для печати (план 121 Ф1). */
export interface GroupRosterRow {
  client_id: string;
  name: string;
  age: number | null;
  representative_name: string | null;
  phone: string | null;
}

export interface GroupRoster {
  group: { id: string; name: string };
  branch: { id: string; name: string } | null;
  teacher_name: string | null;
  month: string;
  occurrences: Array<{ id: string; scheduled_at: string }>;
  schedule_note: string | null;
  /**
   * Месяц, из которого взят состав. Обычно предыдущий; при пустом предыдущем —
   * последний месяц с посещениями (у школы бывают перерывы). null — посещений
   * не было никогда.
   */
  roster_month: string | null;
  /** Сколько человек в составе — записанные на занятия ЭТОГО месяца. */
  enrolled_now: number;
  rows: GroupRosterRow[];
}

export function getGroupRoster(groupId: string, month: string): Promise<GroupRoster> {
  return api.get<GroupRoster>(`/schedule/groups/${groupId}/roster?month=${month}`);
}

/**
 * Та же ведомость файлом Excel (правка владельца 02.09) — админ сам раздвигает
 * колонки, пока печатный лист доводится.
 *
 * Прямой `fetch`, а не `api.get`: JSON-хелпер отдаёт не-JSON ответ как ТЕКСТ, и
 * двоичный xlsx приехал бы битым. Ошибку при этом разбираем так же, как он, —
 * иначе окно молча ничего не скачает.
 *
 * Имя файла передаётся аргументом, хотя сервер ставит его в Content-Disposition:
 * `a.download` у blob-ссылки заголовок не читает вовсе.
 */
export async function downloadGroupRosterXlsx(
  groupId: string,
  month: string,
  fileName: string,
): Promise<void> {
  const url = `${BASE_URL}/schedule/groups/${encodeURIComponent(groupId)}/roster.xlsx?month=${encodeURIComponent(month)}`;
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    let problem: Record<string, unknown> = {};
    try {
      problem = (await response.json()) as Record<string, unknown>;
    } catch {
      // Тело не JSON (502 от nginx) — хватит статуса.
    }
    throw new ApiError(response.status, problem);
  }
  const blob = await response.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = fileName;
  a.click();
  // Отзываем сразу после клика: ссылка уже отдана загрузчику браузера, а
  // невозвращённый blob-URL держит файл в памяти вкладки до её закрытия.
  URL.revokeObjectURL(href);
}

export function createSeries(body: CreateSeriesBody): Promise<CreateSeriesResult> {
  return api.post<CreateSeriesResult>('/schedule/series', body);
}

/**
 * Тело создания РАЗОВОГО занятия (POST /schedule/occurrences).
 *   - group_id опционален: для individual/rental/self_training бэкенд сам
 *     создаёт/находит системную группу; для 'group' — обязателен.
 *   - teacher_id: null/пропуск для аренды (rental); клиент — только individual/rental.
 */
export interface CreateOccurrenceBody {
  group_id?: string;
  branch_id: string;
  hall_id: string;
  teacher_id?: string | null;
  occurrence_kind?: OccurrenceKind;
  /** ISO-8601 c offset. */
  scheduled_at: string;
  duration_minutes?: number;
  max_attendees?: number;
  /** Клиент, которого сразу записать на занятие (individual/rental). */
  client_id?: string;
  /** Абонемент записи; не задан — «запись без услуги» (без списания). */
  subscription_id?: string;
}

export interface CreateOccurrenceResult {
  id: string;
}

/** Создание одного (разового) занятия — POST /schedule/occurrences. */
export function createOccurrence(body: CreateOccurrenceBody): Promise<CreateOccurrenceResult> {
  return api.post<CreateOccurrenceResult>('/schedule/occurrences', body);
}

/**
 * Пакетное создание серий — по одной серии на день недели (A1.2).
 * Каждый item имеет ту же форму, что и body {@link createSeries}.
 */
export interface CreateSeriesBatchResult {
  series: Array<{ id: string; occurrences_generated: number }>;
}

export function createSeriesBatch(items: CreateSeriesBody[]): Promise<CreateSeriesBatchResult> {
  return api.post<CreateSeriesBatchResult>('/schedule/series/batch', { items });
}

// ---------------------------------------------------------------------------
// Аренда залов (rentals) — отдельный слой бронирований поверх сетки (A1.1).
// Backend отдаёт snake_case в { rentals: [...] }; наружу — camelCase RentalItem.
// ---------------------------------------------------------------------------

export type RentalStatus = string;

/** Транспорт бронирования аренды (snake_case с backend). */
interface RentalDto {
  id: string;
  branch_id: string;
  hall_id: string;
  hall_name: string;
  renter_client_id: string;
  renter_name: string | null;
  from_time: string;
  to_time: string;
  price_kopecks: number;
  status: RentalStatus;
}

/** Бронирование аренды зала (camelCase для UI). */
export interface RentalItem {
  id: string;
  branchId: string;
  hallId: string;
  hallName: string;
  renterClientId: string;
  renterName: string | null;
  fromTime: string;
  toTime: string;
  priceKopecks: number;
  status: RentalStatus;
}

export interface ListRentalsParams {
  /** ISO-8601 c offset. */
  from: string;
  to: string;
  /** Филиалы шапки (план 106, мультивыбор); пусто — все. */
  branchIds?: string[];
}

function mapRental(dto: RentalDto): RentalItem {
  return {
    id: dto.id,
    branchId: dto.branch_id,
    hallId: dto.hall_id,
    hallName: dto.hall_name,
    renterClientId: dto.renter_client_id,
    renterName: dto.renter_name,
    fromTime: dto.from_time,
    toTime: dto.to_time,
    priceKopecks: dto.price_kopecks,
    status: dto.status,
  };
}

export function listRentals(params: ListRentalsParams): Promise<RentalItem[]> {
  const sp = new URLSearchParams();
  sp.set('from', params.from);
  sp.set('to', params.to);
  setBranchParams(sp, params.branchIds);
  return api
    .get<{ rentals: RentalDto[] }>(`/schedule/rentals?${sp.toString()}`)
    .then((r) => r.rentals.map(mapRental));
}

// ---------------------------------------------------------------------------
// Настройки сетки расписания (rows / часы) — snake_case на обоих концах (A1.1).
// ---------------------------------------------------------------------------

export interface ScheduleSettings {
  grid_start_hour: number;
  grid_end_hour: number;
  slot_minutes: number;
  // Правила списания при поздней отмене записи. Опциональны для обратной
  // совместимости с моками/старым кэшем; сервер всегда возвращает их (дефолт 24/1).
  cancel_free_window_hours?: number;
  free_late_cancellations?: number;
  // Кулдаун пробного при продаже через кассу (дней, 0 = выключен; дефолт 180).
  trial_cooldown_days?: number;
  // Тумблер самозаписи без абонемента (docs/service/49 + ревизия 51: лимита нет).
  no_sub_booking_enabled?: boolean;
}

export function getScheduleSettings(): Promise<ScheduleSettings> {
  return api.get<ScheduleSettings>('/schedule/settings');
}

export function putScheduleSettings(body: ScheduleSettings): Promise<ScheduleSettings> {
  return api.put<ScheduleSettings>('/schedule/settings', body);
}

// ---------------------------------------------------------------------------
// Attendance (карточка занятия) — enroll / mark-visit / enrolled list.
// Контракт сверен с apps/api attendance.controller (см. задачу).
// ---------------------------------------------------------------------------

export type EnrollmentStatus =
  | 'scheduled'
  | 'attended'
  | 'no_show'
  | 'cancelled'
  | 'no_show_charged';

/** Абонемент, привязанный к записи (enriched в enrollments endpoint). */
export interface EnrollmentSubscription {
  id: string;
  template_name: string;
  sessions_remaining: number | null;
  sessions_total: number | null;
  expires_at: string | null;
  status: string;
  /**
   * Продажа, которую можно доплатить до абонемента (план 120 Б), либо null.
   * Сервер заполняет её, ТОЛЬКО когда доплата возможна прямо сейчас — смена
   * продажи ещё открыта. Клиент пришёл на разовое и тут же решил взять
   * абонемент: это и есть основной сценарий (просьба владельца 03.09.2026).
   */
  upgradable_sale_id: string | null;
}

/** Обогащённая запись клиента на occurrence. */
export interface OccurrenceEnrollment {
  attendance_id: string;
  status: EnrollmentStatus;
  is_waitlist: boolean;
  client_id: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  phone: string | null;
  birth_date: string | null;
  /**
   * Тип карточки записанного (план 138 Ф4). Карточка представителя — не
   * учащийся: в составе занятия она выглядела обычной ученицей, и абонемент
   * продали маме вместо ребёнка (разбор Куматренко 08.09.2026).
   */
  client_type: 'client' | 'representative' | 'client_representative';
  /**
   * Дети записанного — непусто ТОЛЬКО у `client_type='representative'`
   * (план 138 A5). У остальных строк сервер всегда отдаёт `[]`.
   * `age` — полные годы; null, если дата рождения ребёнка не заполнена.
   */
  wards: Array<{ id: string; name: string; age: number | null }>;
  /** Кто водит ребёнка — звонить нужно взрослому (план 104 Б6). */
  representatives: Array<{ id: string; name: string; phone: string | null }>;
  subscription: EnrollmentSubscription | null;
  /** Запись отменена каскадом отмены самого занятия (план 61 П1). */
  cancelled_by_occurrence: boolean;
  /** Доставка автоуведомления об отмене: null — чата нет / не уведомляли. */
  notify_status: 'sent' | 'pending' | 'failed' | null;
  notify_sent_at: string | null;
  /**
   * Последний визит ДО сегодняшнего дня; null — визитов не было («новый»).
   * Считается тем же кодом, что и правило пробного, поэтому метка в ведомости
   * и ответ кассы не расходятся (план 127 Ф2).
   */
  last_visit_at: string | null;
  /**
   * Запись без услуги, но у клиента есть абонемент, который спишется на этом
   * занятии (08.09.2026). Сервер заполняет только для `scheduled` без
   * привязки; строка предлагает «Привязать» одним нажатием.
   */
  applicable_subscription: {
    id: string;
    template_name: string | null;
    sessions_remaining: number | null;
    expires_at: string | null;
  } | null;
}

export interface EnrollClientBody {
  client_id: string;
  occurrence_id: string;
  subscription_id?: string;
  accept_waitlist: boolean;
  /** Записать вопреки пересечению по времени — после подтверждения (план 114). */
  allow_overlap?: boolean;
}

export interface EnrollClientResult {
  attendance_id: string;
  is_waitlist: boolean;
}

export function listEnrollments(occurrenceId: string): Promise<OccurrenceEnrollment[]> {
  const qs = `?occurrence_id=${encodeURIComponent(occurrenceId)}`;
  return api
    .get<{ data: OccurrenceEnrollment[] }>(`/attendance/enrollments${qs}`)
    .then((r) => r.data);
}

/**
 * Кандидаты на занятие — правая панель «Рабочего дня» (план 121 Ф2):
 * держатели абонемента этой группы и ходившие в неё за два месяца, кроме уже
 * записанных.
 */
export interface OccurrenceCandidate {
  client_id: string;
  first_name: string;
  last_name: string;
  middle_name: string | null;
  phone: string | null;
  birth_date: string | null;
  representatives: Array<{ id: string; name: string; phone: string | null }>;
  source: 'subscription' | 'recent';
  last_visit_at: string | null;
  subscription: {
    id: string;
    template_name: string | null;
    sessions_remaining: number | null;
    expires_at: string | null;
  } | null;
}

export function listOccurrenceCandidates(occurrenceId: string): Promise<OccurrenceCandidate[]> {
  const qs = `?occurrence_id=${encodeURIComponent(occurrenceId)}`;
  return api
    .get<{ data: OccurrenceCandidate[] }>(`/attendance/candidates${qs}`)
    .then((r) => r.data);
}

/**
 * Отметка «пришёл» тому, кто НЕ записан (план 121 Ф2): сервер сам подбирает
 * абонемент и создаёт запись. Абонемента нет — `attendance.no_entitlement`, и
 * интерфейс предлагает продать.
 */
export function markVisitDirect(clientId: string, occurrenceId: string): Promise<unknown> {
  return api.post('/attendance', {
    client_id: clientId,
    occurrence_id: occurrenceId,
    status: 'attended',
    source: 'manual',
  });
}

/** Отметка визита (attended/no_show/scheduled) / удаление записи (cancelled). */
/**
 * Привязать проданный абонемент к записи «без услуги»: иначе отметка «Пришёл»
 * не спишет занятие (списание резолвит абонемент только у записи, где он уже
 * указан). Правка владельца 2026-07-19, сценарий аренды.
 */
export function attachSubscriptionToAttendance(
  attendanceId: string,
  subscriptionId: string,
): Promise<{ subscription_id: string }> {
  return api.patch<{ subscription_id: string }>(
    `/attendance/${encodeURIComponent(attendanceId)}/subscription`,
    { subscription_id: subscriptionId },
  );
}

/**
 * Смена статуса записи. `confirmLateCancel` — согласие сотрудника на ПОЗДНЮЮ
 * отмену (внутри окна школы, план 114): без него сервер отвечает 409
 * `attendance.late_cancel_confirm`, а не отменяет молча.
 */
export function markVisit(
  attendanceId: string,
  status: EnrollmentStatus,
  confirmLateCancel = false,
): Promise<unknown> {
  return api.patch<unknown>(`/attendance/${encodeURIComponent(attendanceId)}/correct`, {
    status,
    ...(confirmLateCancel ? { confirm_late_cancel: true } : {}),
  });
}

export function enrollClient(body: EnrollClientBody): Promise<EnrollClientResult> {
  return api.post<EnrollClientResult>('/attendance/enroll', body);
}

// ---------------------------------------------------------------------------
// Изменение в расписании (A1.3) — правка одного занятия (occurrence) или
// периода (серия с указанной даты). Контракт сверен с schedule.controller:
//   PATCH /schedule/occurrences/:id            — один день (teacher/hall/cap/group)
//   PATCH /schedule/series/:id                 — период (from_date + поля)
//   POST  /schedule/series/:id/cancel-future   — отмена будущих занятий серии
//   PATCH /schedule/occurrences/:id/cancel     — отмена одного занятия (reason?)
//   PATCH /schedule/occurrences/:id/move       — перенос одного занятия
// ---------------------------------------------------------------------------

/**
 * Поля правки одного занятия (occurrence). Все опциональны — шлём только изменённое.
 * `public_note` — клиент-видимое примечание к изменению (механизм «Изменение в
 * расписании», 2026-07-09): сохраняется в class_occurrences.public_note.
 */
export interface EditOccurrenceBody {
  teacher_id?: string;
  hall_id?: string;
  max_attendees?: number;
  group_id?: string;
  public_note?: string;
}

/** Поля правки периода серии — те же, плюс обязательная from_date (YYYY-MM-DD). */
export interface EditSeriesBody {
  from_date: string;
  teacher_id?: string;
  hall_id?: string;
  max_attendees?: number;
  group_id?: string;
  public_note?: string;
}

/** Отмена будущих занятий серии с (опциональной) даты. */
export interface CancelFutureBody {
  from_date?: string;
  public_note?: string;
}

export interface CancelFutureResult {
  cancelled_count: number;
}

/** Отмена одного занятия — опциональная причина (+ клиент-видимое примечание). */
export interface CancelOccurrenceBody {
  reason?: string;
  public_note?: string;
}

/** Прогул, выбранный для записанного при «Никто не пришёл» (план 153 §2.3). */
export type NotHeldMarkStatus = 'no_show' | 'no_show_charged';

/**
 * «Не состоялось» с причиной (план 153 §2.8). Причина и выбор сообщения —
 * обязательны; `marks` — только у «Никто не пришёл» и ровно весь живой состав;
 * `comment` — внутренний, клиенту не уходит; клиенту — только `public_note`.
 */
export interface MarkNotHeldBody {
  reason: NotHeldReason;
  comment?: string;
  marks?: Array<{ attendance_id: string; status: NotHeldMarkStatus }>;
  notify: 'none' | 'enrolled';
  public_note?: string;
}

/** Итог рассылки из ответа «Не состоялось»: счётчики по факту постановки либо сбой. */
export type NotHeldNotifyOutcome =
  | { requested: 'none' }
  | ({ requested: 'enrolled' } & (NotifyOccurrenceChangeResult | { error: string }));

export interface MarkNotHeldResult {
  ok: true;
  marks_changed: number;
  charged: number;
  unmarked_cancelled: number;
  notify: NotHeldNotifyOutcome;
}

export interface RevertNotHeldResult {
  ok: true;
  status: 'scheduled' | 'completed';
  restored: number;
}

/** Перенос одного занятия — новое время (ISO) + опционально зал/педагог/примечание. */
export interface MoveOccurrenceBody {
  new_scheduled_at: string;
  new_hall_id?: string;
  new_teacher_id?: string;
  public_note?: string;
}

export interface MoveOccurrenceResult {
  new_occurrence_id: string;
}

/** Правка одного занятия — PATCH /schedule/occurrences/:id. */
export function editOccurrence(id: string, body: EditOccurrenceBody): Promise<unknown> {
  return api.patch<unknown>(`/schedule/occurrences/${encodeURIComponent(id)}`, body);
}

/** Правка периода серии — PATCH /schedule/series/:id/edit (from_date включительно). */
export function editSeries(seriesId: string, body: EditSeriesBody): Promise<unknown> {
  return api.patch<unknown>(`/schedule/series/${encodeURIComponent(seriesId)}/edit`, body);
}

/** Отмена будущих занятий серии — POST /schedule/series/:id/cancel-future. */
export function cancelFutureOccurrences(
  seriesId: string,
  body: CancelFutureBody,
): Promise<CancelFutureResult> {
  return api.post<CancelFutureResult>(
    `/schedule/series/${encodeURIComponent(seriesId)}/cancel-future`,
    body,
  );
}

/** Отмена одного занятия — PATCH /schedule/occurrences/:id/cancel. */
export function cancelOccurrence(id: string, body: CancelOccurrenceBody): Promise<unknown> {
  return api.patch<unknown>(`/schedule/occurrences/${encodeURIComponent(id)}/cancel`, body);
}

/** «Не состоялось» с причиной — PATCH /schedule/occurrences/:id/not-held (план 153). */
export function markOccurrenceNotHeld(
  id: string,
  body: MarkNotHeldBody,
): Promise<MarkNotHeldResult> {
  return api.patch<MarkNotHeldResult>(
    `/schedule/occurrences/${encodeURIComponent(id)}/not-held`,
    body,
  );
}

/** «Занятие было» — возврат из «Не состоялось» (план 153 §2.7). */
export function revertOccurrenceNotHeld(id: string): Promise<RevertNotHeldResult> {
  return api.patch<RevertNotHeldResult>(
    `/schedule/occurrences/${encodeURIComponent(id)}/not-held/revert`,
    {},
  );
}

/** Удаление одного занятия — DELETE /schedule/occurrences/:id (occurrence:delete). */
export function deleteOccurrence(id: string): Promise<unknown> {
  return api.delete<unknown>(`/schedule/occurrences/${encodeURIComponent(id)}`);
}

export interface DeleteChainResult {
  deleted: number;
  kept_conducted: number;
  /** Прошлые непроведённые занятия, оставленные из-за окна прав роли. */
  kept_past?: number;
  series_deleted: boolean;
}

/** Удаление всей цепочки — DELETE /schedule/series/:id (непроведённые + серия). */
export function deleteSeriesChain(seriesId: string): Promise<DeleteChainResult> {
  return api.delete<DeleteChainResult>(`/schedule/series/${encodeURIComponent(seriesId)}`);
}

/** Перенос одного занятия — PATCH /schedule/occurrences/:id/move. */
export function moveOccurrence(
  id: string,
  body: MoveOccurrenceBody,
): Promise<MoveOccurrenceResult> {
  return api.patch<MoveOccurrenceResult>(
    `/schedule/occurrences/${encodeURIComponent(id)}/move`,
    body,
  );
}

// ---------------------------------------------------------------------------
// Уведомление клиентов об изменении в расписании (правки владельца 2026-07-09).
// Колокольчик на блоке занятия → выбор типа изменения → рассылка записанным
// клиентам в привязанные Telegram/MAX-диалоги (модуль messenger).
// ---------------------------------------------------------------------------

/** Тип изменения занятия для клиентского уведомления. */
export type OccurrenceChangeNotifyKind =
  | 'cancelled'
  | 'moved'
  | 'teacher_changed'
  | 'not_held'
  | 'hall_changed'
  | 'capacity_changed';

export interface NotifyOccurrenceChangeResult {
  /** Клиентов, которым сообщение реально поставлено в очередь (план 153 §2.5). */
  sent: number;
  /** Клиентов без привязанного диалога — уведомить не удалось. */
  skipped: number;
  /**
   * Клиентов, которым это же уведомление уже отправляли раньше (занятый
   * dedup-ключ). Опционально: старый сервер поля не отдаёт.
   */
  deduped?: number;
}

/** Уровень оповещения: записанным на занятие / всем клиентам филиала. */
export type NotificationLevel = 'enrolled' | 'branch';

/** Доп. параметры оповещения (уровень + клиент-видимое примечание). */
export interface NotifyOccurrenceChangeOpts {
  notification_level?: NotificationLevel;
  note?: string;
}

/** Уведомить клиентов об изменении — POST /messenger/notify-occurrence-change. */
export function notifyOccurrenceChange(
  occurrenceId: string,
  changeKind: OccurrenceChangeNotifyKind,
  opts?: NotifyOccurrenceChangeOpts,
): Promise<NotifyOccurrenceChangeResult> {
  const body: {
    occurrence_id: string;
    change_kind: OccurrenceChangeNotifyKind;
    notification_level?: NotificationLevel;
    note?: string;
  } = {
    occurrence_id: occurrenceId,
    change_kind: changeKind,
  };
  if (opts?.notification_level) body.notification_level = opts.notification_level;
  if (opts?.note) body.note = opts.note;
  return api.post<NotifyOccurrenceChangeResult>('/messenger/notify-occurrence-change', body);
}
